import { useState, useCallback, useEffect } from 'react';
import {
    getCardsByStatus,
    updateCard,
    createCard
} from '../api';

export const useEmergencyManager = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [activeEmergencyId, setActiveEmergencyId] = useState<string | null>(null);

    // 1. 현재 진행 중인 업무가 긴급 건인지 확인
    const checkActiveTaskStatus = useCallback(async () => {
        try {
            const res = await getCardsByStatus({ status: 'IN_PROGRESS' });
            const runningCards = res.data._embedded?.getList || [];

            if (runningCards.length > 0) {
                const activeCard = runningCards[0];
                // ✨ isEmergency 필드로 판단
                if (activeCard.isEmergency) {
                    setActiveEmergencyId(activeCard.id);
                    return;
                }
            }
            setActiveEmergencyId(null);
        } catch (e) {
            console.error("업무 상태 체크 실패:", e);
            setActiveEmergencyId(null);
        }
    }, []);

    // 2. 긴급 시작 (Start)
    const startEmergency = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            const runningCardsRes = await getCardsByStatus({ status: 'IN_PROGRESS' });
            const runningCards = runningCardsRes.data._embedded?.getList || [];

            let currentRunningId = '';
            if (runningCards.length > 0) {
                currentRunningId = runningCards[0].id;
                await updateCard({ id: currentRunningId, status: 'TODO' });
            }

            const timestamp = new Date().toLocaleTimeString();

            // ✨ 새 긴급 카드 생성 (isEmergency: true 설정)
            const newCardRes = await createCard({
                title: `🚨 긴급 요청 업무 [${timestamp}]`,
                description: '<p>긴급 버튼을 통해 생성된 업무입니다.</p>',
                priority: 0,
                isEmergency: true, // 긴급 건임을 표시
                prevEmergencyId: currentRunningId || undefined
            });

            if (newCardRes.data?.id) {
                await updateCard({ id: newCardRes.data.id, status: 'IN_PROGRESS', labels: [{ name: 'URGENT', color: '#e06c75' }], state: 'PROCESS' });
            }

            window.location.reload();

        } catch (error) {
            console.error("긴급 시작 실패:", error);
            alert("긴급 모드 시작 중 오류가 발생했습니다.");
            setIsLoading(false);
        }
    }, [isLoading]);

    // 3. 긴급 종료 및 복귀 (Stop & Resume)
    const stopEmergency = useCallback(async () => {
        if (isLoading) return;
        setIsLoading(true);

        try {
            const runningCardsRes = await getCardsByStatus({ status: 'IN_PROGRESS' });
            const runningCards = runningCardsRes.data._embedded?.getList || [];

            if (runningCards.length === 0) return;

            const currentCard = runningCards[0];
            const prevCardId = currentCard.prevEmergencyId;

            const endTime = new Date().toLocaleTimeString();
            const updatedTitle = `${currentCard.title} ~ [${endTime}]`;

            // ✨ 현재 업무 종료 (isEmergency: false로 변경)
            await updateCard({
                id: currentCard.id,
                status: 'TODO',
                title: updatedTitle,
                isEmergency: false // 종료 시 일반 카드로 전환
            });

            // 이전 업무가 있었다면 재개
            if (prevCardId) {
                await updateCard({ id: prevCardId, status: 'IN_PROGRESS' });
            }

            window.location.reload();

        } catch (error) {
            console.error("긴급 종료 실패:", error);
            setIsLoading(false);
        }
    }, [isLoading]);

    // 4. 전역 단축키 설정 및 상태 체크
    useEffect(() => {
        checkActiveTaskStatus();

        const handleKeyDown = (e: KeyboardEvent) => {
            // Alt + Shift + E: 긴급 시작
            if (e.altKey && e.shiftKey && e.code === 'KeyE') {
                e.preventDefault();
                startEmergency();
            }
            // Alt + Shift + S: 긴급 종료 (S는 Stop의 약자)
            if (e.altKey && e.shiftKey && e.code === 'KeyS') {
                e.preventDefault();
                stopEmergency();
            }
        };

        const onFocus = () => checkActiveTaskStatus();
        window.addEventListener('focus', onFocus);
        window.addEventListener('keydown', handleKeyDown);

        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('keydown', handleKeyDown);
        };
    }, [checkActiveTaskStatus, startEmergency, stopEmergency]);

    return {
        startEmergency,
        stopEmergency,
        // ✨ 긴급 업무가 존재하기만 하면 종료 버튼 활성화
        hasActiveEmergency: !!activeEmergencyId,
        isLoading
    };
};